import React from 'react';
import PropTypes from 'prop-types';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from 'views/components';
import {Translate} from 'views/module';

import moment from 'moment-timezone';

const InvitationDialog = (props) => {
  const {caregivers, onClose, onSend} = props;

  const [stage, setStage] = React.useState('CONFIRM');
  const [results, setResults] = React.useState({});

  const torontoHour = moment().tz('America/Toronto').hour();
  const retryable = caregivers.filter((caregiver) => {
    const result = results[caregiver.caregiverId];
    return result?.state === 'FAILED' && (result.error?.status || 500) >= 500;
  });

  const send = async (targets) => {
    setStage('SENDING');

    for (const caregiver of targets) {
      setResults((current) => ({...current, [caregiver.caregiverId]: {state: 'SENDING'}}));

      try {
        await onSend(caregiver.caregiverId);
        setResults((current) => ({...current, [caregiver.caregiverId]: {state: 'SENT'}}));
      } catch (error) {
        setResults((current) => ({...current, [caregiver.caregiverId]: {state: 'FAILED', error}}));
      }
    }

    setStage('DONE');
  };

  return (
    <Dialog onClose={stage === 'SENDING' ? undefined : () => onClose(stage !== 'CONFIRM')}>
      <DialogTitle onClose={stage === 'SENDING' ? undefined : () => onClose(stage !== 'CONFIRM')}>
        <Translate expression="career.jobDetail.Candidates.InvitationDialog.Title" />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{pt: 1}}>
          {
            stage === 'CONFIRM' &&
            <Alert severity="info">
              <Translate
                expression={`career.jobDetail.Candidates.InvitationDialog.${
                  torontoHour < 9 ? 'CallToday' : torontoHour < 20 ? 'CallNow' : 'CallTomorrow'
                }`}
              />
            </Alert>
          }
          {
            stage === 'SENDING' &&
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                <Translate
                  expression="career.jobDetail.Candidates.InvitationDialog.Sending"
                  substitute={{
                    done: Object.values(results).filter((result) => result.state !== 'SENDING').length,
                    total: caregivers.length,
                  }}
                />
              </Typography>
            </Stack>
          }
          <Stack spacing={1}>
            {
              caregivers.map((caregiver) => {
                const result = results[caregiver.caregiverId];

                return (
                  <Stack
                    key={caregiver.caregiverId}
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="baseline"
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {`${caregiver.firstName} ${caregiver.lastName}`}
                    </Typography>
                    {
                      stage !== 'CONFIRM' &&
                      <Typography
                        variant="body2"
                        color={result?.state === 'SENT' ? 'success.main' : result?.state === 'FAILED' ? 'error.main' : 'text.secondary'}
                        sx={{textAlign: 'right'}}
                      >
                        {
                          !result &&
                          <Translate expression="career.jobDetail.Candidates.InvitationDialog.Pending" />
                        }
                        {
                          result?.state === 'SENT' &&
                          <Translate expression="career.jobDetail.Candidates.InvitationDialog.Sent" />
                        }
                        {
                          result?.state === 'FAILED' &&
                          <Translate
                            expression={`career.jobDetail.Candidates.InvitationDialog.Errors.${
                              ['CGR_001', 'INV_002', 'INV_003', 'INV_006', 'INV_007', 'INV_009', 'JOB_001', 'JOB_011']
                                  .includes(result.error?.code) ? result.error.code : 'Default'
                            }`}
                          />
                        }
                      </Typography>
                    }
                  </Stack>
                );
              })
            }
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        {
          stage === 'CONFIRM' &&
          <>
            <Button variant="outlined" onClick={() => onClose(false)}>
              <Translate expression="generic.Cancel" />
            </Button>
            <Button onClick={() => send(caregivers)}>
              <Translate expression="career.jobDetail.Candidates.InvitationDialog.Send" />
            </Button>
          </>
        }
        {
          stage === 'DONE' &&
          <>
            {
              retryable.length > 0 &&
              <Button variant="outlined" onClick={() => send(retryable)}>
                <Translate expression="career.jobDetail.Candidates.InvitationDialog.Retry" substitute={{count: retryable.length}} />
              </Button>
            }
            <Button onClick={() => onClose(true)}>
              <Translate expression="generic.Close" />
            </Button>
          </>
        }
      </DialogActions>
    </Dialog>
  );
};

InvitationDialog.propTypes = {
  caregivers: PropTypes.arrayOf(PropTypes.shape({
    caregiverId: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
};

export default React.memo(InvitationDialog);
